// Карусели [data-provide="slider"].
//
// Заменяет slick (и вместе с ним jQuery). Прокрутку делает сам браузер —
// у контейнера overflow-x: auto и scroll-snap, поэтому карусель листается
// пальцем и колесом даже без JS. Скрипт добавляет точки, автопрокрутку
// и подставляет число видимых слайдов в --carousel-slides.
//
// Поддерживаемые data-атрибуты те же, что читал slick:
// data-slides-to-show, data-slides-to-scroll, data-dots, data-autoplay,
// data-autoplay-speed, data-center-mode.
//
// Дополнительно data-breakpoints="576:2,768:3,992:4" задаёт свои пороги
// для конкретной карусели: до 576px — 2 слайда, до 768px — 3 и т.д.
// Без атрибута действуют пороги по умолчанию (BREAKPOINTS).

(function () {
    "use strict";

    interface Breakpoint {
        maxWidth: number;
        show: number;
        scroll: number;
    }

    interface Settings {
        show: number;
        scroll: number;
    }

    // Пороги повторяют responsive-настройки из прежней инициализации slick:
    // до 576px — один слайд, до 768px — три.
    const BREAKPOINTS: Breakpoint[] = [
        { maxWidth: 576, show: 1, scroll: 1 },
        { maxWidth: 768, show: 3, scroll: 2 }
    ];

    const DEFAULT_AUTOPLAY_SPEED = 3000;

    function number(value: string | undefined, fallback: number): number {
        const parsed = parseInt(value ?? "", 10);
        return isNaN(parsed) || parsed < 1 ? fallback : parsed;
    }

    // Разбирает data-breakpoints вида "576:2,768:3". Шаг прокрутки на пороге
    // равен числу видимых слайдов — листаем целыми страницами.
    function parseBreakpoints(value: string | undefined): Breakpoint[] | null {
        if (!value) {
            return null;
        }

        const result: Breakpoint[] = [];

        for (const part of value.split(",")) {
            const pair = part.split(":");
            const maxWidth = parseInt(pair[0], 10);
            const show = parseInt(pair[1], 10);

            if (!isNaN(maxWidth) && maxWidth > 0 && !isNaN(show) && show > 0) {
                result.push({ maxWidth: maxWidth, show: show, scroll: show });
            }
        }

        return result.length
            ? result.sort(function (a, b) { return a.maxWidth - b.maxWidth; })
            : null;
    }

    class Carousel {
        private readonly root: HTMLElement;
        private readonly slides: HTMLElement[];

        private readonly showRequested: number;
        private readonly scrollRequested: number;
        private readonly breakpoints: Breakpoint[];
        private readonly centered: boolean;
        private readonly autoplay: boolean;
        private readonly autoplaySpeed: number;
        private readonly dotsEnabled: boolean;

        private wrapper!: HTMLElement;
        private dotsBox: HTMLElement | null = null;
        private dots: HTMLButtonElement[] = [];

        private show = 1;
        private step = 1;
        private pages = 1;

        private timer = 0;
        private resizeTimer = 0;
        private scrollTimer = 0;

        constructor(root: HTMLElement) {
            this.root = root;
            this.slides = Array.prototype.slice.call(root.children) as HTMLElement[];

            this.showRequested = number(root.dataset.slidesToShow, 1);
            this.scrollRequested = number(root.dataset.slidesToScroll, 1);
            this.breakpoints = parseBreakpoints(root.dataset.breakpoints) ?? BREAKPOINTS;
            this.centered = root.dataset.centerMode === "true";
            this.autoplay = root.dataset.autoplay === "true";
            this.autoplaySpeed = number(root.dataset.autoplaySpeed, DEFAULT_AUTOPLAY_SPEED);
            this.dotsEnabled = root.dataset.dots === "true";

            this.mount();
        }

        private mount(): void {
            this.root.classList.add("carousel-track");

            if (this.centered) {
                this.root.classList.add("carousel-centered");
            }

            this.slides.forEach(function (slide) {
                slide.classList.add("carousel-slide");
            });

            // Обёртка нужна, чтобы точки жили рядом с прокручиваемой лентой,
            // а не внутри неё.
            const wrapper = document.createElement("div");
            wrapper.className = "carousel";
            this.root.parentNode?.insertBefore(wrapper, this.root);
            wrapper.appendChild(this.root);
            this.wrapper = wrapper;

            if (this.dotsEnabled) {
                this.dotsBox = document.createElement("div");
                this.dotsBox.className = "carousel-dots";
                wrapper.appendChild(this.dotsBox);
            }

            this.layout();

            window.addEventListener("resize", () => this.relayout());

            // Одного расчёта при загрузке мало: ширина может устояться позже
            // (поздние стили, шрифты, скрытый до поры контейнер). Тогда число
            // слайдов останется от нулевой ширины — наблюдатель это исправит.
            if (typeof ResizeObserver === "function") {
                new ResizeObserver(() => this.relayout()).observe(this.root);
            }

            this.root.addEventListener("scroll", () => {
                window.clearTimeout(this.scrollTimer);
                this.scrollTimer = window.setTimeout(() => this.syncDots(), 100);
            }, { passive: true });

            this.startAutoplay();
        }

        private relayout(): void {
            window.clearTimeout(this.resizeTimer);
            this.resizeTimer = window.setTimeout(() => this.layout(), 150);
        }

        /** Число видимых слайдов и шаг прокрутки для текущей ширины окна. */
        private settings(): Settings {
            const width = window.innerWidth;

            for (const breakpoint of this.breakpoints) {
                if (width <= breakpoint.maxWidth) {
                    return {
                        show: Math.min(breakpoint.show, this.showRequested),
                        scroll: Math.min(breakpoint.scroll, this.scrollRequested)
                    };
                }
            }

            return { show: this.showRequested, scroll: this.scrollRequested };
        }

        private layout(): void {
            const settings = this.settings();

            this.show = settings.show;
            this.step = Math.min(settings.scroll, this.show);
            this.root.style.setProperty("--carousel-slides", String(this.show));

            this.pages = Math.max(1, Math.ceil((this.slides.length - this.show) / this.step) + 1);

            this.renderDots();
            this.syncDots();
        }

        private renderDots(): void {
            const dotsBox = this.dotsBox;

            if (!dotsBox) {
                return;
            }

            // Одна страница — листать нечего, точки только мешают.
            if (this.pages < 2) {
                dotsBox.innerHTML = "";
                this.dots = [];
                return;
            }

            if (this.dots.length === this.pages) {
                return;
            }

            dotsBox.innerHTML = "";
            this.dots = [];

            for (let i = 0; i < this.pages; i++) {
                const page = i;
                const dot = document.createElement("button");
                dot.type = "button";
                dot.className = "carousel-dot";
                dot.setAttribute("aria-label", String(page + 1));

                dot.addEventListener("click", () => {
                    this.stopAutoplay();
                    this.goTo(page);
                });

                dotsBox.appendChild(dot);
                this.dots.push(dot);
            }
        }

        private slideWidth(): number {
            const slide = this.slides[0];
            return slide ? slide.getBoundingClientRect().width : 0;
        }

        private currentPage(): number {
            const width = this.slideWidth() * this.step;

            if (!width) {
                return 0;
            }

            const page = Math.round(this.root.scrollLeft / width);
            return Math.max(0, Math.min(page, this.pages - 1));
        }

        private syncDots(): void {
            const current = this.currentPage();

            this.dots.forEach(function (dot, index) {
                const active = index === current;
                dot.classList.toggle("is-active", active);
                dot.setAttribute("aria-current", active ? "true" : "false");
            });
        }

        private goTo(page: number): void {
            const left = page * this.step * this.slideWidth();

            // behavior не указываем намеренно: тогда действует CSS-свойство
            // scroll-behavior, которое отключается при prefers-reduced-motion.
            this.root.scrollTo({ left: left });
        }

        private next(): void {
            const page = this.currentPage() + 1;
            this.goTo(page >= this.pages ? 0 : page);
        }

        private startAutoplay(): void {
            if (!this.autoplay || this.timer) {
                return;
            }

            // Автопрокрутка — движение без запроса пользователя, поэтому
            // при включённом «уменьшить движение» её не запускаем.
            if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
                return;
            }

            this.timer = window.setInterval(() => {
                if (!document.hidden && this.pages > 1) {
                    this.next();
                }
            }, this.autoplaySpeed);

            // Как и у slick, наведение и фокус приостанавливают показ.
            this.wrapper.addEventListener("mouseenter", () => this.stopAutoplay());
            this.wrapper.addEventListener("focusin", () => this.stopAutoplay());
        }

        private stopAutoplay(): void {
            window.clearInterval(this.timer);
            this.timer = 0;
        }
    }

    function init(): void {
        document.querySelectorAll<HTMLElement>('[data-provide="slider"]').forEach(function (root) {
            if (!root.children.length) {
                return;
            }

            new Carousel(root);
        });
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init);
    } else {
        init();
    }
})();
